/**
 * Fehlergrenze — fängt ab, was beim Rendern einer Ansicht schiefgeht.
 *
 * Ohne sie nimmt React bei jedem unbehandelten Fehler den **gesamten** Baum
 * vom Bildschirm. Aus einem Tippfehler in einer einzigen Ansicht wird dann
 * eine weiße Seite ohne Kopfzeile, ohne Navigation und ohne jeden Hinweis
 * darauf, was fehlt — nicht bedienbar und nicht meldbar, weil niemand sagen
 * kann, woran es lag.
 *
 * Deshalb steht hier zweierlei:
 *
 * 1. **Die Grenze sitzt um den Inhaltsbereich, nicht um die Anwendung.**
 *    Kopfzeile und Navigation bleiben stehen; eine kaputte Ansicht kostet
 *    genau diese Ansicht, und man kommt mit einem Klick woanders hin.
 *
 * 2. **Die Meldung nennt den Fehler im Klartext.** Ein „Es ist ein Fehler
 *    aufgetreten" hilft niemandem. Sichtbar sind Meldung und Herkunft, dazu
 *    die Fassung — das ist genau das, was in einer Fehlermeldung stehen muss,
 *    damit sie ohne Rückfrage bearbeitet werden kann.
 *
 * Der Schlüssel `zuruecksetzen` hängt am Pfad: wer nach einem Fehler
 * weiternavigiert, soll die neue Ansicht sehen und nicht die alte Meldung.
 */

import { Component, type ErrorInfo, type ReactNode } from 'react';
import { stil } from './stil';

interface Eigenschaften {
  children: ReactNode;
  /** Wechselt dieser Wert, verschwindet die Meldung — typisch der Pfad. */
  zuruecksetzen?: string;
  /** Fassung der Oberfläche, damit eine Meldung zuordenbar ist. */
  version?: string;
}

interface Lage {
  fehler: Error | null;
  herkunft: string | null;
  /** Der Wert von `zuruecksetzen`, bei dem der Fehler auftrat. */
  bei: string | undefined;
}

export class Fehlergrenze extends Component<Eigenschaften, Lage> {
  override state: Lage = { fehler: null, herkunft: null, bei: undefined };

  static getDerivedStateFromError(fehler: Error): Partial<Lage> {
    return { fehler };
  }

  /**
   * Die Ableitung oben kennt den Pfad nicht — sie bekommt nur den Fehler.
   * Deshalb wird hier nachgetragen, wo er auftrat; sonst gilt der Fehler
   * sofort als „von woanders" und die Meldung verschwindet, ehe sie jemand
   * gelesen hat.
   */
  override componentDidCatch(fehler: Error, hinweis: ErrorInfo) {
    this.setState({ herkunft: hinweis.componentStack ?? null, bei: this.props.zuruecksetzen });
    // Bleibt in der Browserkonsole nachlesbar, samt Quellkarte.
    console.error('Ansicht abgebrochen:', fehler, hinweis.componentStack);
  }

  override componentDidUpdate(vorher: Eigenschaften) {
    if (
      this.state.fehler
      && this.props.zuruecksetzen !== vorher.zuruecksetzen
      && this.props.zuruecksetzen !== this.state.bei
    ) {
      this.setState({ fehler: null, herkunft: null, bei: undefined });
    }
  }

  override render() {
    const { fehler, herkunft } = this.state;
    if (!fehler) return this.props.children;

    /* Die oberste eigene Zeile aus dem Komponentenstapel. React setzt dort die
       Namen der Bausteine untereinander; die erste ist die Ansicht, in der es
       gerissen ist — die einzige Zeile, die ohne Quellkarte etwas aussagt. */
    const ansicht = herkunft?.split('\n').map((z) => z.trim()).find((z) => z.startsWith('at '));

    return (
      <div style={stil('flex:1;min-inline-size:0;padding:24px')} role="alert">
        <div style={stil(
          'max-inline-size:70ch;padding:20px 22px;background:var(--surface-card);'
          + 'border:1px solid var(--danger);border-radius:var(--r-lg)',
        )}>
          <div style={stil('display:flex;align-items:flex-start;gap:10px')}>
            <svg aria-hidden="true" width="20" height="20" viewBox="0 0 24 24" fill="none"
                 stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"
                 style={stil('flex:none;margin-block-start:2px;color:var(--danger)')}>
              <path d="M12 3l9.5 17H2.5L12 3zM12 9v5M12 17.2v.1" />
            </svg>
            <div style={stil('min-inline-size:0')}>
              <h2 style={stil(
                'margin:0;font-size:17px;line-height:24px;font-weight:600;color:var(--text-primary)',
              )}>
                Diese Ansicht konnte nicht angezeigt werden
              </h2>
              <p style={stil(
                'margin:8px 0 0;font-size:14px;line-height:21px;color:var(--text-secondary)',
              )}>
                Die übrige Anwendung läuft weiter — über die Navigation kommen Sie
                woanders hin. Bitte melden Sie den folgenden Text unverändert weiter.
              </p>
            </div>
          </div>

          <pre style={stil(
            'margin:14px 0 0;padding:12px 14px;background:var(--surface-sunken);'
            + 'border-radius:var(--r-md);font-family:var(--mono);font-size:12.5px;'
            + 'line-height:18px;color:var(--text-primary);white-space:pre-wrap;'
            + 'overflow-wrap:anywhere',
          )}>
            {fehler.name}: {fehler.message}
            {ansicht ? `\n${ansicht}` : ''}
            {this.props.version ? `\nFassung ${this.props.version}` : ''}
          </pre>

          <button
            type="button"
            onClick={() => this.setState({ fehler: null, herkunft: null, bei: undefined })}
            style={stil(
              'margin-block-start:14px;min-block-size:36px;padding:0 14px;'
              + 'background:var(--brand-strong);color:var(--text-on-brand);border:none;'
              + 'border-radius:var(--r-md);font-size:14px;font-weight:600;cursor:pointer',
            )}
          >
            Erneut versuchen
          </button>
        </div>
      </div>
    );
  }
}
